import { ShoppingDataLayerProduct } from '@makro-shopping/use-product-card-logic/dist/types/product.type';
import {
  CarouselImpressionEventProps,
  CarouselPDPClickEventProps,
  CarouselClickEventProps,
} from '../types/analytics.types';
import { StoreBasketItemDataType } from '../types/carousel.types';
import { RecommendationBanner, RecommendationResponse } from '../types/common';
import { Product, ProductSearchResult } from '../types/product.types';
import { WEBVIEW_MESSAGE_TYPE } from '../utils/constants';

export const isReactNativeWebView = () => typeof window !== 'undefined' && window?.ReactNativeWebView;

class BrowserEventService {
  private hasSentMFEReady = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  triggerSeeAllAction = (message: any, isFromBasket?: boolean) => {
    this.postMessage(
      {
        type: WEBVIEW_MESSAGE_TYPE.seeAll,
        message: message,
      },
      isFromBasket || false,
    );
  };

  onProductClick = (
    product: Product & { position: number },
    analytics: CarouselPDPClickEventProps,
    isFromBasket?: boolean,
    scrollToVariants?: boolean,
  ) => {
    this.postMessage(
      {
        type: WEBVIEW_MESSAGE_TYPE.navigatepdp,
        message: {
          product,
          analytics,
          scrollToVariants: Boolean(scrollToVariants),
        },
      },
      isFromBasket || false,
    );
  };

  triggerRedirectToLogin = (isFromBasket?: boolean) => {
    this.postMessage(
      {
        type: WEBVIEW_MESSAGE_TYPE.redirectToLogin,
      },
      isFromBasket || false,
    );
  };

  sendProductCount = (count: number, isFromBasket?: boolean) => {
    this.postMessage(
      {
        type: WEBVIEW_MESSAGE_TYPE.productCount,
        message: count,
      },
      isFromBasket || false,
    );
  };

  sendIntialProducts = (data: RecommendationResponse, isFromBasket?: boolean) => {
    this.postMessage(
      {
        type: WEBVIEW_MESSAGE_TYPE.INITIAL_PRODUCTS,
        message: data,
      },
      isFromBasket || false,
    );
  };

  refreshBasket = (isFromBasket?: boolean) => {
    this.postMessage(
      {
        type: WEBVIEW_MESSAGE_TYPE.REFRESH_BASKET,
      },
      isFromBasket || false,
    );
  };

  updateBasket = (data: unknown, isFromBasket?: boolean) => {
    this.postMessage(
      {
        type: WEBVIEW_MESSAGE_TYPE.UPDATE_BASKET,
        message: data,
      },
      isFromBasket || false,
    );
  };

  addBasketItemsToLocal = (data: StoreBasketItemDataType, isFromBasket?: boolean) => {
    this.postMessage(
      {
        type: WEBVIEW_MESSAGE_TYPE.ADD_BASKET_ITEM_TO_LOCAL_STORAGE,
        message: data,
      },
      isFromBasket || false,
    );
  };

  triggerAddToCartAnalytics = (eventName: string, params?: unknown, isFromBasket?: boolean) => {
    this.postMessage(
      {
        type: WEBVIEW_MESSAGE_TYPE.TRIGGER_BASKET_ANALYTICS,
        eventName,
        params,
      },
      isFromBasket || false,
    );
  };

  triggerCarouselImpressionEvent = (analytics: CarouselImpressionEventProps, isFromBasket?: boolean) => {
    this.postMessage(
      {
        type: WEBVIEW_MESSAGE_TYPE.carouselImpression,
        message: analytics,
      },
      isFromBasket || false,
    );
  };

  triggerCarouselScroll = (analytics: CarouselClickEventProps, scrollDirection?: string, isFromBasket?: boolean) => {
    this.postMessage(
      {
        type: WEBVIEW_MESSAGE_TYPE.CAROUSEL_SCROLL,
        message: analytics,
        scrollDirection: scrollDirection,
      },
      isFromBasket || false,
    );
  };

  requestFeatureFlags = (isFromBasket?: boolean) => {
    this.postMessage(
      {
        type: WEBVIEW_MESSAGE_TYPE.requestFeatureFlags,
      },
      isFromBasket || false,
    );
  };

  heightChange = (height: number, isFromBasket?: boolean, isVerticalScroll?: boolean) => {
    this.postMessage(
      {
        type: WEBVIEW_MESSAGE_TYPE.heightChange,
        message: Math.ceil(height),
        isVerticalScroll,
      },
      isFromBasket || false,
    );
  };

  sendRecommendations = (data: RecommendationResponse, isFromBasket?: boolean) => {
    this.postMessage(
      {
        type: WEBVIEW_MESSAGE_TYPE.sendRecommendations,
        message: data,
      },
      isFromBasket || false,
    );
  };

  carouselEmpty = (from: string, isFromBasket?: boolean) => {
    this.postMessage(
      {
        type: WEBVIEW_MESSAGE_TYPE.carouselEmpty,
        from,
        message: true,
      },
      isFromBasket || false,
    );
  };
  carouselNotEmpty = (from: string, isFromBasket?: boolean) => {
    this.postMessage(
      {
        type: WEBVIEW_MESSAGE_TYPE.carouselEmpty,
        from,
        message: false,
      },
      isFromBasket || false,
    );
  };

  iframeReady = (
    isReady: boolean,
    isFromBasket?: boolean,
    isProductCardV3?: boolean,
    isVerticalScroll?: boolean,
  ) => {
    this.postMessage(
      {
        type: WEBVIEW_MESSAGE_TYPE.iframeReady,
        message: isReady,
        isProductCardV3,
        isVerticalScroll,
      },
      isFromBasket || false,
    );
  };

  horizonPaginationHeight = (height: number, isFromBasket?: boolean) => {
    this.postMessage(
      {
        type: WEBVIEW_MESSAGE_TYPE.HORIZON_PAGINATION_HEIGHT,
        message: Math.ceil(height),
      },
      isFromBasket || false,
    );
  };

  horizonBottomSheetState = (isOpen: boolean, isFromBasket?: boolean) => {
    this.postMessage(
      {
        type: WEBVIEW_MESSAGE_TYPE.HORIZON_BOTTOM_SHEET_STATE,
        message: isOpen,
      },
      isFromBasket || false,
    );
  };

  navigateToApplyCard = () => {
    this.postMessage({
      type: WEBVIEW_MESSAGE_TYPE.REDIRECT_TO_APPLY_CARD,
    });
  };

  navigateToPromotionPlp = (
    product: ProductSearchResult | Product | ShoppingDataLayerProduct,
    promotionData?: {
      collectionId?: string;
      collectionHandle?: string;
      isComplexPromotion?: boolean;
      productId?: string;
    },
  ) => {
    this.postMessage({
      type: WEBVIEW_MESSAGE_TYPE.REDIRECT_TO_PROMOTION_PLP,
      product,
      promotionData,
    });
  };

  triggerProductOptionSelectorAnalytics = (eventName: string, params?: unknown) => {
    this.postMessage({
      type: WEBVIEW_MESSAGE_TYPE.TRIGGER_OPTION_SELECTOR_ANALYTICS,
      eventName,
      params,
    });
  };

  triggerRecommendationBannerAnalytics = (eventName: string, params?: unknown) => {
    this.postMessage({
      type: WEBVIEW_MESSAGE_TYPE.TRIGGER_RECOMMENDATION_BANNER_ANALYTICS,
      eventName,
      params,
    });
  };

  navigateRecommendationBanner = (banner: RecommendationBanner) => {
    this.postMessage({
      type: WEBVIEW_MESSAGE_TYPE.NAVIGATE_RECOMMENDATION_BANNER,
      message: banner,
    });
  };

  setIframeHeight = (isProductCardV3?: boolean, isVariantModalOpen?: boolean) => {
    this.postMessage({
      type: WEBVIEW_MESSAGE_TYPE.SET_IFRAME_HEIGHT,
      isProductCardV3,
      isVariantModalOpen,
    });
  };

  isMFEReady = () => {
    if (this.hasSentMFEReady) return;
    this.hasSentMFEReady = true;
    this.postMessage({
      type: WEBVIEW_MESSAGE_TYPE.IS_MFE_READY,
      message: true,
    });
  }

  private postMessage(eventData: unknown, isFromBasket?: boolean) {
    const isInIframe = window && window.self !== window.top;
    try {
      if (window?.ReactNativeWebView && !isFromBasket && !isInIframe) {
        console.info('BrowserEventService: Posting message to React Native WebView');
        window?.ReactNativeWebView?.postMessage(JSON.stringify(eventData));
      } else {
        console.info('BrowserEventService: Posting message to parent window');
        window.parent.postMessage(eventData, '*');
      }

      console.info('BrowserEventService: Message posted successfully', eventData);
    } catch (error) {
      console.error('BrowserEventService: Failed to post message', error);
    }
  }
}

const SendBrowserEvent = new BrowserEventService();

export default SendBrowserEvent;
